import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Send, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [
  { name: "smtp_host", label: "SMTP host", ph: "smtp.yourdomain.ca" },
  { name: "smtp_port", label: "SMTP port", ph: "587" },
  { name: "smtp_user", label: "SMTP username", ph: "user@yourdomain.ca" },
  { name: "smtp_password", label: "SMTP password", type: "password", ph: "••••••••" },
  { name: "from_email", label: "From email (verified domain)", ph: "events@yourdomain.ca" },
  { name: "from_name", label: "From name", ph: "Show Us Your Moves" },
  { name: "notify_email", label: "Team notification email", ph: "team@yourdomain.ca" },
];

export default function EmailSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/email-settings").then((r) => setForm(r.data || {})).catch(() => setForm({}));
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/email-settings", form); toast.success("Email settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Enter a recipient email"); return; }
    setTesting(true);
    try { await api.post("/email/test", { to: testTo }); toast.success(`Test email sent to ${testTo}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (!form) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2CACD4]" /></div>;

  const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4] disabled:opacity-60";

  return (
    <div className="max-w-2xl" data-testid="admin-email-settings">
      <h1 className="font-display font-black uppercase text-3xl flex items-center gap-3"><Mail className="text-[#2CACD4]" /> Email (SMTP)</h1>
      <p className="mt-1 text-muted-foreground text-sm">Configure your own SMTP server to send RSVP confirmations and calendar invites. (IMAP is only for receiving mail — sending uses SMTP.)</p>
      {!isAdmin && <p className="mt-4 rounded-xl bg-muted p-3 text-sm text-muted-foreground">Only admins can change email settings.</p>}

      <label className="mt-6 flex items-center gap-3 cursor-pointer">
        <input type="checkbox" disabled={!isAdmin} checked={!!form.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-5 w-5 rounded accent-[#2CACD4] cursor-pointer" data-testid="email-enabled" />
        <span className="font-semibold">Enable outgoing email</span>
      </label>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {FIELDS.map((f) => (
          <div key={f.name} className={f.name === "smtp_host" || f.name === "from_email" ? "sm:col-span-2" : ""}>
            <label className="block text-sm font-semibold mb-1.5">{f.label}</label>
            <input type={f.type || "text"} disabled={!isAdmin} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.ph} className={inputCls} data-testid={`email-${f.name}`} />
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-6">
        <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
          <input type="checkbox" disabled={!isAdmin} checked={form.use_tls !== false} onChange={(e) => set("use_tls", e.target.checked)} className="h-4 w-4 accent-[#2CACD4] cursor-pointer" data-testid="email-usetls" /> Use STARTTLS (port 587)
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
          <input type="checkbox" disabled={!isAdmin} checked={!!form.use_ssl} onChange={(e) => set("use_ssl", e.target.checked)} className="h-4 w-4 accent-[#2CACD4] cursor-pointer" data-testid="email-usessl" /> Use SSL (port 465)
        </label>
      </div>

      {isAdmin && (
        <button onClick={save} disabled={saving} className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white hover:bg-[#2CACD4] hover:text-slate-950 font-bold px-6 py-3 transition-colors cursor-pointer disabled:opacity-60" data-testid="email-save">
          {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={18} /> Save settings</>}
        </button>
      )}

      <div className="mt-10 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display font-black uppercase text-lg">Send a test email</h2>
        <p className="mt-1 text-sm text-muted-foreground">Save your settings first, then send yourself a test.</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@yourdomain.ca" className={inputCls} data-testid="email-test-to" />
          <button onClick={sendTest} disabled={testing || !isAdmin} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#DB1A5C] text-white hover:bg-[#0F172A] font-bold px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap" data-testid="email-test-send">
            {testing ? <Loader2 className="animate-spin" size={18} /> : <><Send size={16} /> Send test</>}
          </button>
        </div>
      </div>
    </div>
  );
}
